import * as React from 'react';
import { ChangeEvent, FC, useEffect, useState } from 'react';

import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from '@tanstack/react-query';
import { Edit, Package, Trash2 } from 'react-feather';
import { NavLink, useLocation } from 'react-router-dom';

import { Button, LinearProgress, Switch, TablePagination } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
  useGetOrganizations,
  useDeleteOrganization,
  useUpdateOrgStatus,
} from '../../../api-http/organizations';
import { Nullable } from '../../../types';
import { AlertBar } from '../../../components/AlertBar';
import { useAuth } from '../../../context/auth-context';
import { OrderEnum, SortEnum } from '../../../types/pagination.types';
import { displayDate } from '../../../util/dateTime';
import { notifyMsg } from '../../../configs';
import { useSnack } from '../../../util/useSnack';
import { useTable } from '../../../util/useTable';
import { BaseOrganization } from '../../../types/organization.types';
import { UpdateOrganizationModal } from './components/UpdateOrganizationModal';
import { DialogModal } from '../../../components';
import { SetPackagesModal } from './components/SetPackagesModal';
import { UserRole } from '../../../types/user.types';
import { useIsUserRole } from '../../../util/useIsUserRole';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../../components/TableTheme';

interface OrganizationsTableProp {
  selectedOrgName: Nullable<string>;
}

export const OrganizationsTable: FC<OrganizationsTableProp> = ({
  selectedOrgName,
}) => {
  const [open, setOpen] = useState(false);
  const disableSetPackage = useIsUserRole([
    UserRole.user,
    UserRole.layoutEditor,
    UserRole.dataEditor,
  ]);
  const { currentUser, switchedOrgId, setSwitchedOrgId } = useAuth();
  const queryClient = useQueryClient();
  const { showSuccessSnack, showErrSnack } = useSnack();
  const confirm = useConfirm();
  const { sortOnClick, pagingParams, setPage, tableProps } = useTable(true);
  const { sortby, order } = pagingParams;
  const location = useLocation();

  const [orgUpdateModal, setOrgUpdateModal] = useState(false);
  const [activeOrg, setActiveOrg] = useState<Nullable<BaseOrganization>>(null);

  useEffect(() => {
    if (selectedOrgName) {
      setPage(0);
    }
  }, [selectedOrgName]);

  const {
    data: paginateData,
    isLoading,
    error,
    refetch,
  } = useGetOrganizations(
    { ...pagingParams, search: selectedOrgName || '' },
    { onError: (err) => showErrSnack(err) },
  );

  const {
    mutate: updateStatus,
    isLoading: statusIsLoading,
    error: statusError,
  } = useUpdateOrgStatus({
    onSuccess(response) {
      if (!response.status && switchedOrgId === response._id) {
        setSwitchedOrgId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['organization/all'] });
      showSuccessSnack(
        response.status
          ? notifyMsg.ORGANIZATION_ENABLE_SUCCESS
          : notifyMsg.ORGANIZATION_DISABLE_SUCCESS,
      );
      refetch();
    },
    onError: (err) => showErrSnack(err),
  });

  const {
    mutate: deleteOrganization,
    isLoading: deleteIsLoading,
    error: deleteError,
  } = useDeleteOrganization({
    onSuccess: ({ organizationId }) => {
      if (switchedOrgId === organizationId) {
        setSwitchedOrgId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['organization/all'] });
      showSuccessSnack(notifyMsg.ORGANIZATION_DELETE_SUCCESS);
      refetch();
    },
    onError: (err) => showErrSnack(err),
  });

  const onOrgUpdate = (organization: BaseOrganization) => {
    setActiveOrg(organization);
    setOrgUpdateModal(true);
  };

  const switchHandler = (event: ChangeEvent<HTMLInputElement>, orgId: string) =>
    updateStatus({ id: orgId || '', status: event.target.checked });

  const handleDelete = async (organization: BaseOrganization) => {
    try {
      await confirm({
        title: `Delete ${organization.name}?`,
        description:
          'This permanently deletes the organization and all of its KVS data, schedulers, assets, devices, forms, slides, presentations, users, and related records. This action cannot be undone.',
        confirmationText: 'Delete permanently',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      deleteOrganization({ id: organization._id });
    } catch (error) {}
  };

  return (
    <>
      {(statusIsLoading || deleteIsLoading || isLoading) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg} />
      <AlertBar severity="error" msg={statusError?.msg} />
      <AlertBar severity="error" msg={deleteError?.msg} />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.name}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Organization Name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">Email</StyledTableCell>
              <StyledTableCell align="right">Pin Code</StyledTableCell>
              <StyledTableCell align="right">
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last Updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created Date
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">Select Package</StyledTableCell>
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginateData?.data.map((organization) => {
              return (
                <TableRow
                  key={organization._id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <NavLink
                      to={`/admin/users/${organization._id}`}
                      state={{
                        search: location?.search,
                      }}
                    >
                      {organization?.name}
                    </NavLink>
                  </TableCell>
                  <TableCell align="right">{organization?.email}</TableCell>
                  <TableCell align="right">{organization?.pinCode}</TableCell>
                  <TableCell align="right">
                    {displayDate(organization?.updatedAt)}
                  </TableCell>
                  <TableCell align="right">
                    {displayDate(organization?.createdAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      onClick={() => setOpen(true)}
                      disabled={disableSetPackage}
                    >
                      <Package size={16} />
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Button onClick={() => onOrgUpdate(organization)}>
                      <Edit size={16} />
                    </Button>
                    <Button
                      color="error"
                      aria-label={`Delete ${organization.name}`}
                      title="Delete organization"
                      disabled={
                        currentUser?.organization?._id === organization._id ||
                        deleteIsLoading
                      }
                      onClick={() => handleDelete(organization)}
                    >
                      <Trash2 size={16} style={{ marginRight: 5 }} />
                      Delete
                    </Button>
                    <Switch
                      id={organization?._id}
                      disabled={
                        currentUser?.organization?._id == organization?._id
                      }
                      checked={organization?.status}
                      onChange={(event) =>
                        switchHandler(event, organization._id)
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={paginateData?.pagination.total === 0}
          count={paginateData?.pagination.total || 0}
        />
      </TableContainer>

      <UpdateOrganizationModal
        onClose={() => setOrgUpdateModal(false)}
        open={orgUpdateModal}
        onFinished={() => refetch()}
        organizationData={activeOrg}
      />

      <DialogModal
        title="Select Package"
        onClose={() => setOpen(false)}
        open={open}
        style={{ width: '30%' }}
      >
        <SetPackagesModal onFinished={() => setOpen(false)} />
      </DialogModal>
    </>
  );
};
